import { ACTION_ICONS } from "../../config/siteConfig";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { airportsService } from "../../services/airports.service";
import DatePicker from "react-multi-date-picker";
import { Modal } from "react-bootstrap";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ContentLoader from "../../components/loader/ContentLoader";
import DetailView from "../../components/location/DetailView";
import { supplierBlackoutsService } from "../../services/supplier-blackouts.service";
import { suppliersService } from "../../services/suppliers.service";
import { airlinesService } from "../../services/airlines.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const toArray = (resData) =>
  Array.isArray(resData) ? resData : resData?.data || [];

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const TRIP_TYPE_OPTIONS = [
  { value: "one_way", label: "One Way" },
  { value: "round_trip", label: "Round Trip" },
  { value: "multi_city", label: "Multi City" },
];

const ROUTE_TYPE_OPTIONS = [
  { value: "domestic", label: "Domestic" },
  { value: "international", label: "International" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const optionLabel = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "-";

// sector comes as "ORG-DST"
const parseSector = (sector) => {
  const [origin, destination] = String(sector || "").split(/\W+/);
  return { origin: origin || "-", destination: destination || "-" };
};

const formatDate = (d) => (d ? String(d).slice(0, 10) : "-");

const SupplierInventoryBlackout = () => {
  const { start, complete } = useLoadingBar();

  const [loading, setLoading] = useState(false);
  const [blackoutRes, setBlackoutRes] = useState({
    pagination: {},
    blackouts: [],
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = {
    supplier_code: "",
    airline_code: "",
    trip_type: "",
    route_type: "",
    blackout_from: "",
    blackout_to: "",
    status: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [show, setShow] = useState(false);
  const [viewBlackout, setViewBlackout] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [supplierOptions, setSupplierOptions] = useState([]);
  const [airlineOptions, setAirlineOptions] = useState([]);

  useEffect(() => {
    loadBlackouts();
  }, [pagingLimit, pageValue, appliedFilters]);

  useEffect(() => {
    loadSupplierOptions();
    loadAirlineOptions();
  }, []);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadSupplierOptions = async () => {
    try {
      const res = await suppliersService.list({
        page: 1,
        status: "active",
      });
      setSupplierOptions(
        toArray(res?.resData).map((s) => ({
          value: s.supplier_code,
          label: `${s.agency_name} (${s.supplier_code})`,
        })),
      );
    } catch (err) {
      setSupplierOptions([]);
    }
  };

  const loadAirlineOptions = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      setAirlineOptions(
        toArray(res?.resData).map((a) => ({
          value: a.airline_code,
          label: `${a.airline_name} (${a.airline_code})`,
        })),
      );
    } catch (err) {
      setAirlineOptions([]);
    }
  };

  const loadBlackouts = async () => {
    try {
      setLoading(true);
      start();
      const res = await supplierBlackoutsService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setBlackoutRes({
          pagination: res.resData?.pagination || {},
          blackouts: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleStatusChange = async (blackout, newStatus) => {
    if (newStatus === blackout?.status) return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark this blackout for ${blackout?.supplier?.agency_name || blackout?.supplier_code} as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await supplierBlackoutsService.updateStatus(blackout?.id, {
        status: newStatus,
      });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadBlackouts();
    }
  };

  const handleDeleteBlackout = async (blackout) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete blackout?",
      text: `Are you sure you want to delete this blackout for ${blackout?.supplier?.agency_name || blackout?.supplier_code}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await supplierBlackoutsService.delete(blackout?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadBlackouts();
    }
  };

  const handleViewBlackout = async (blackoutId) => {
    setShow(true);
    setViewBlackout(null);
    try {
      setViewLoading(true);
      const res = await supplierBlackoutsService.details(blackoutId);
      if (res?.success) {
        setViewBlackout(res.resData);
      } else {
        toast.error(res?.message);
        setShow(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShow(false);
    } finally {
      setViewLoading(false);
    }
  };

  // ---- Add Supplier Inventory Blackout (popup) ----
  const initialForm = {
    supplier_code: "",
    airline_code: "",
    origin: null,
    destination: null,
    city: "",
    trip_type: "",
    route_type: "",
    blackout_from: "",
    blackout_to: "",
    remarks: "",
    status: "active",
  };
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  // edit mode: id of the blackout being edited (null = add)
  const [editId, setEditId] = useState(null);
  const isEdit = !!editId;

  const openAdd = () => {
    setEditId(null);
    setForm(initialForm);
    setShowAdd(true);
  };

  const airportOption = (code) => (code ? { value: code, label: code } : null);

  const openEdit = async (blackoutId) => {
    setEditId(blackoutId);
    setForm(initialForm);
    setShowAdd(true);
    try {
      setSaving(true);
      const res = await supplierBlackoutsService.details(blackoutId);
      if (res?.success) {
        const b = res.resData;
        // sector is "ORG-DST"; only prefill when it parses cleanly
        const m = String(b?.sector || "").match(
          /^([A-Za-z]{3})\W+([A-Za-z]{3})$/,
        );
        setForm({
          supplier_code: b?.supplier_code || "",
          airline_code: b?.airline_code || "",
          origin: m ? airportOption(m[1].toUpperCase()) : null,
          destination: m ? airportOption(m[2].toUpperCase()) : null,
          city: b?.city || "",
          trip_type: b?.trip_type || "",
          route_type: b?.route_type || "",
          blackout_from:
            formatDate(b?.blackout_from) === "-"
              ? ""
              : formatDate(b?.blackout_from),
          blackout_to:
            formatDate(b?.blackout_to) === "-"
              ? ""
              : formatDate(b?.blackout_to),
          remarks: b?.remarks || "",
          status: b?.status || "active",
        });
      } else {
        toast.error(res?.message);
        setShowAdd(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShowAdd(false);
    } finally {
      setSaving(false);
    }
  };

  const closeAdd = () => {
    if (!saving) setShowAdd(false);
  };

  const handleFormChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // origin / destination: airports > list (status = active), searched on the server
  const loadAirportOptions = async (search = "") => {
    try {
      const res = await airportsService.list({
        page: 1,
        status: "active",
        ...(search ? { search } : {}),
      });
      if (res?.success) {
        return toArray(res.resData).map((a) => ({
          value: a.airport_code,
          label: `${a.city_name} (${a.airport_code}) - ${a.airport_name}`,
        }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      return [];
    }
  };

  const validateForm = () => {
    if (!form.supplier_code) return "Supplier is required";
    return null;
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update blackout?" : "Save blackout?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;
    try {
      setSaving(true);
      const payload = {
        supplier_code: form.supplier_code,
        ...(form.airline_code ? { airline_code: form.airline_code } : {}),
        ...(form.origin ? { origin: form.origin.value } : {}),
        ...(form.destination ? { destination: form.destination.value } : {}),
        ...(form.city.trim() ? { city: form.city.trim() } : {}),
        ...(form.trip_type ? { trip_type: form.trip_type } : {}),
        ...(form.route_type ? { route_type: form.route_type } : {}),
        ...(form.blackout_from ? { blackout_from: form.blackout_from } : {}),
        ...(form.blackout_to ? { blackout_to: form.blackout_to } : {}),
        ...(form.remarks.trim() ? { remarks: form.remarks.trim() } : {}),
        status: form.status,
      };
      const res = isEdit
        ? await supplierBlackoutsService.update(
            editId,
            (({ supplier_code, ...rest }) => rest)(payload),
          )
        : await supplierBlackoutsService.create(payload);
      if (res?.success) {
        await withReactContent(Swal).fire({
          title: "Success",
          text: res.message,
          icon: "success",
        });
        setShowAdd(false);
        if (isEdit || pageValue === 1) loadBlackouts();
        else setPageValue(1);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  };

  const blackouts = blackoutRes?.blackouts || [];
  const totalRecords = blackoutRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const activeCount = blackouts.filter((v) => v?.status === "active").length;
  const fromRecord =
    totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5)
      return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Supplier Code", key: "supplier_code" },
    { label: "Supplier", key: "supplier.agency_name" },
    { label: "Airline Code", key: "airline_code" },
    { label: "Airline", key: "airline.airline_name" },
    { label: "Sector", key: "sector" },
    { label: "City", key: "city" },
    { label: "Trip Type", key: "trip_type" },
    { label: "Route Type", key: "route_type" },
    { label: "Fare Type", key: "fare_type" },
    { label: "Blackout From", key: "blackout_from" },
    { label: "Blackout To", key: "blackout_to" },
    { label: "Remarks", key: "remarks" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  const selectFilter = (options, key, placeholder, col = "col-6 col-md-2") => (
    <div className={col}>
      <Select
        options={options}
        value={options.find((o) => o.value === filters[key]) || null}
        onChange={(o) => setFilters({ ...filters, [key]: o ? o.value : "" })}
        placeholder={placeholder}
        isClearable
        menuPortalTarget={document.body}
        styles={selectStyles}
      />
    </div>
  );

  const actionBtn = (bg, color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    color,
    border: "none",
    padding: 0,
  });

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4
              className="m-0 fw-bold"
              style={{ color: "var(--page-heading)", fontSize: 20 }}
            >
              Supplier Inventory Blackouts
            </h4>
            <nav
              className="d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: 12, color: "#475569" }}
            >
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/setting" style={{ color: "#475569" }}>
                Settings
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Supplier Inventory Blackouts List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={openAdd}
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{
                background: "var(--btn-bg)",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <i className="bi bi-plus-lg"></i> Add Supplier Inventory Blackout
            </button>
            <CSVLink
              data={blackouts}
              headers={csvHeaders}
              filename="supplier-blackouts.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                color: "#444",
              }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form
          onSubmit={handleApply}
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              {selectFilter(
                supplierOptions,
                "supplier_code",
                "Select Supplier",
                "col-6 col-md-3",
              )}
              {selectFilter(
                airlineOptions,
                "airline_code",
                "Select Airline",
                "col-6 col-md-3",
              )}
              {selectFilter(TRIP_TYPE_OPTIONS, "trip_type", "Select Trip Type")}
              {selectFilter(
                ROUTE_TYPE_OPTIONS,
                "route_type",
                "Select Route Type",
              )}
              {selectFilter(STATUS_FILTER_OPTIONS, "status", "Select Status")}
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.blackout_from || null}
                  onChange={(d) =>
                    setFilters({
                      ...filters,
                      blackout_from: d ? d.format("YYYY-MM-DD") : "",
                    })
                  }
                  format="YYYY-MM-DD"
                  placeholder="Blackout from"
                  maxDate={filters.blackout_to || undefined}
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.blackout_to || null}
                  onChange={(d) =>
                    setFilters({
                      ...filters,
                      blackout_to: d ? d.format("YYYY-MM-DD") : "",
                    })
                  }
                  format="YYYY-MM-DD"
                  placeholder="Blackout to"
                  minDate={filters.blackout_from || undefined}
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{
                    background: "var(--btn-bg)",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{
                    background: "#fff",
                    border: "1px solid #e8edf5",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Blackouts
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{activeCount}</b> Active on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{blackouts.length - activeCount}</b> Inactive on this page
          </span>
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 14, overflow: "hidden" }}
        >
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", 
                      "Supplier",
                      "Airline",
                      "Origin",
                      "Destination",
                      "City",
                      "Trip Type",
                      "Route Type",
                      "Blackout From",
                      "Blackout To",
                      "Status",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {blackouts.map((blackout, sno_i) => (
                    <tr
                      key={blackout?.id}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 36,
                              height: 36,
                              background: "#e8f0fe",
                              flexShrink: 0,
                            }}
                          >
                            <i
                              className="bi bi-person-badge text-primary"
                              style={{ fontSize: 14 }}
                            ></i>
                          </div>
                          <div>
                            <div
                              className="fw-semibold text-dark"
                              style={{ fontSize: 13 }}
                            >
                              {blackout?.supplier?.agency_name || "-"}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: 11 }}
                            >
                              {blackout?.supplier_code}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="fw-medium" style={{ fontSize: 13 }}>
                          {blackout?.airline?.airline_name || "-"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {blackout?.airline_code}
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {parseSector(blackout?.sector).origin}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {parseSector(blackout?.sector).destination}
                      </td>
                      <td style={{ fontSize: 13 }}>{blackout?.city || "-"}</td>
                      <td style={{ fontSize: 13 }}>
                        {optionLabel(TRIP_TYPE_OPTIONS, blackout?.trip_type)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {optionLabel(ROUTE_TYPE_OPTIONS, blackout?.route_type)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {formatDate(blackout?.blackout_from)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {formatDate(blackout?.blackout_to)}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={blackout?.status}
                          onChange={(e) =>
                            handleStatusChange(blackout, e.target.value)
                          }
                          style={{
                            width: "auto",
                            minWidth: 0,
                            height: 28,
                            padding: "0 26px 0 10px",
                            backgroundPosition: "right 8px center",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor:
                              blackout?.status === "active"
                                ? "#d4edda"
                                : "#f8d7da",
                            color:
                              blackout?.status === "active"
                                ? "#155724"
                                : "#721c24",
                            border: "none",
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewBlackout(blackout?.id)}
                            title="View"
                            style={actionBtn("#e6f7f1", "#0f8a5f")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.view}`} data-action="view"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openEdit(blackout?.id)}
                            title="Edit"
                            style={actionBtn("#e8f0fe", "var(--btn-bg)")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.edit}`} data-action="edit"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteBlackout(blackout)}
                            title="Delete"
                            style={actionBtn("#fce8e6", "#d93025")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.delete}`} data-action="delete"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && blackouts.length === 0 && (
                    <tr>
                      <td
                        colSpan={12}
                        className="text-center text-muted py-4"
                        style={{ fontSize: 13 }}
                      >
                        No blackouts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{
              borderTop: "1px solid #eef1f6",
              fontSize: 13,
              color: "#6b7a90",
            }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> blackouts
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button
                type="button"
                style={pageBtn(false, false)}
                onClick={handleGoPage}
              >
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      style={pageBtn(n === pageValue, false)}
                      onClick={() => handlePagination(n)}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal centered size="lg" show={showAdd} onHide={closeAdd}>
          <form onSubmit={handleAddSubmit}>
            <Modal.Header closeButton>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {isEdit ? "Update" : "Add"} Supplier Inventory Blackout
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                  {isEdit
                    ? "Update blackout information"
                    : "Block a supplier's inventory for a date range"}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              {(() => {
                const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 6 };
                const inputStyle = {
                  borderRadius: 8,
                  border: "1px solid #c3cad6",
                  fontSize: 13,
                  height: 40,
                  background: "#fff",
                };
                const formSelectStyles = {
                  ...selectStyles,
                  control: (base, state) => ({
                    ...base,
                    borderRadius: 8,
                    border: state.isFocused
                      ? "1px solid var(--btn-bg)"
                      : "1px solid #c3cad6",
                    minHeight: 40,
                    fontSize: 13,
                    boxShadow: state.isFocused
                      ? "0 0 0 3px rgba(14,106,150,.15)"
                      : "none",
                    "&:hover": { borderColor: "var(--btn-bg)" },
                  }),
                  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
                  indicatorsContainer: (base) => ({ ...base, height: 40 }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: 13,
                    background: state.isSelected
                      ? "var(--btn-bg)"
                      : state.isFocused
                        ? "#e6f1f8"
                        : "#fff",
                    color: state.isSelected ? "#fff" : "#1f2a44",
                  }),
                };
                const field = (
                  text,
                  required,
                  node,
                  cls = "col-12 col-md-6",
                ) => (
                  <div className={cls}>
                    <div style={labelStyle}>
                      {text}
                      {required && <span className="text-danger"> *</span>}
                    </div>
                    {node}
                  </div>
                );
                const pick = (options, key, placeholder, clearable = true) => (
                  <Select
                    isDisabled={isEdit && key === "supplier_code"}
                    options={options}
                    value={options.find((o) => o.value === form[key]) || null}
                    onChange={(o) =>
                      setForm({ ...form, [key]: o ? o.value : "" })
                    }
                    placeholder={placeholder}
                    isClearable={clearable}
                    menuPortalTarget={document.body}
                    styles={formSelectStyles}
                  />
                );
                const airport = (key, placeholder) => (
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadAirportOptions}
                    value={form[key]}
                    onChange={(o) => setForm({ ...form, [key]: o || null })}
                    placeholder={placeholder}
                    isClearable
                    menuPortalTarget={document.body}
                    styles={formSelectStyles}
                  />
                );
                const date = (key, placeholder, extra) => (
                  <DatePicker
                    onOpenPickNewDate={false}
                    value={form[key] || null}
                    onChange={(d) =>
                      setForm({
                        ...form,
                        [key]: d ? d.format("YYYY-MM-DD") : "",
                      })
                    }
                    format="YYYY-MM-DD"
                    placeholder={placeholder}
                    editable={false}
                    inputClass="form-control"
                    containerStyle={{ width: "100%" }}
                    style={{ ...inputStyle, width: "100%" }}
                    {...extra}
                  />
                );
                return (
                  <div className="row g-3">
                    {field(
                      "Supplier",
                      true,
                      pick(supplierOptions, "supplier_code", "Select Supplier"),
                    )}
                    {field(
                      "Airline",
                      false,
                      pick(airlineOptions, "airline_code", "Select Airline"),
                    )}
                    {field(
                      "Origin",
                      false,
                      airport("origin", "Search origin airport"),
                    )}
                    {field(
                      "Destination",
                      false,
                      airport("destination", "Search destination airport"),
                    )}
                    {field(
                      "City",
                      false,
                      <input
                        type="text"
                        className="form-control"
                        name="city"
                        placeholder="Enter city"
                        value={form.city}
                        onChange={handleFormChange}
                        style={inputStyle}
                      />,
                    )}
                    {field(
                      "Trip Type",
                      false,
                      pick(
                        TRIP_TYPE_OPTIONS.filter(
                          (o) => o.value !== "multi_city",
                        ),
                        "trip_type",
                        "Select Trip Type",
                      ),
                    )}
                    
                    {field(
                      "Blackout From",
                      false,
                      date("blackout_from", "Select start date", {
                        maxDate: form.blackout_to || undefined,
                      }),
                    )}
                    {field(
                      "Blackout To",
                      false,
                      date("blackout_to", "Select end date", {
                        minDate: form.blackout_from || undefined,
                      }),
                    )}
                    {field(
                      "Route Type",
                      false,
                      pick(
                        ROUTE_TYPE_OPTIONS,
                        "route_type",
                        "Select Route Type",
                      ),
                    )}
                    {field(
                      "Status",
                      false,
                      pick(
                        STATUS_FILTER_OPTIONS,
                        "status",
                        "Select Status",
                        false,
                      ),
                    )}
                    {/* {field(
                      "Remarks",
                      false,
                      <textarea
                        className="form-control"
                        rows={2}
                        name="remarks"
                        placeholder="Reason for blackout"
                        value={form.remarks}
                        onChange={handleFormChange}
                        style={{
                          borderRadius: 8,
                          border: "1px solid #c3cad6",
                          fontSize: 13,
                        }}
                      />,
                      "col-12",
                    )} */}
                  </div>
                );
              })()}
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button
                type="button"
                className="btn"
                onClick={closeAdd}
                disabled={saving}
                style={{
                  background: "#fff",
                  border: "1px solid #c3cad6",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                  color: "#444",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white fw-semibold"
                disabled={saving}
                style={{
                  background: "var(--btn-bg)",
                  border: "none",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                }}
              >
                {saving
                  ? "Saving..."
                  : isEdit
                    ? "Update Supplier Inventory Blackout"
                    : "Add Supplier Inventory Blackout"}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
              borderBottom: "none",
            }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Blackout Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {viewLoading || !viewBlackout ? (
              <ContentLoader loaderType="circleSpin" />
            ) : (
              <DetailView
                fields={[
                  {
                    label: "Supplier",
                    value: viewBlackout.supplier?.agency_name,
                  },
                  { label: "Supplier Code", value: viewBlackout.supplier_code },
                  {
                    label: "Airline",
                    value: viewBlackout.airline?.airline_name,
                  },
                  { label: "Airline Code", value: viewBlackout.airline_code },
                  { label: "Sector", value: viewBlackout.sector },
                  { label: "City", value: viewBlackout.city },
                  {
                    label: "Trip Type",
                    value: optionLabel(
                      TRIP_TYPE_OPTIONS,
                      viewBlackout.trip_type,
                    ),
                  },
                  {
                    label: "Route Type",
                    value: optionLabel(
                      ROUTE_TYPE_OPTIONS,
                      viewBlackout.route_type,
                    ),
                  },
                  { label: "Fare Type", value: viewBlackout.fare_type },
                  {
                    label: "Blackout From",
                    value: formatDate(viewBlackout.blackout_from),
                  },
                  {
                    label: "Blackout To",
                    value: formatDate(viewBlackout.blackout_to),
                  },
                  { label: "Remarks", value: viewBlackout.remarks, full: true },
                  { label: "Created At", value: viewBlackout.created_at },
                  { label: "Status", value: viewBlackout.status, badge: true },
                ]}
              />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default SupplierInventoryBlackout;
